/**
 * c-welcome-popup.js
 *
 * Behaviour for the `c-welcome-popup` section.
 *
 * Opens a few seconds after landing, but never over the age gate: it waits
 * for the gate's `fm_age_verified` cookie, or for its confirm click.
 *
 * Signup is a normal post of the native customer form. Shopify's captcha
 * script has to see that submit and only accepts a real page post (a fetch
 * post is sent to /challenge, even with a token), so the page reloads. To
 * keep that quiet, the scroll position and the email are kept in
 * sessionStorage; the page that comes back restores the scroll, cleans
 * `?customer_posted=true` out of the URL and opens straight on the success
 * state with no entrance animation. The session flag also tells this post
 * apart from the footer newsletter's.
 *
 * Once posted, the code goes on the cart through /cart/update.js, kept
 * alongside any codes already there like Horizon's cart-discount.js does.
 *
 * Cookies:
 *   fm_welcome       'joined' (never again) or 'closed' (pill only, for N days)
 *   fm_welcome_pill  'hidden' once the pill is dismissed too
 */
(function () {
  'use strict';

  var GATE_COOKIE = 'fm_age_verified';
  var STATE_COOKIE = 'fm_welcome';
  var PILL_COOKIE = 'fm_welcome_pill';
  var PENDING_KEY = 'fm_welcome_pending';
  var EMAIL_KEY = 'fm_welcome_email';
  var SCROLL_KEY = 'fm_welcome_scroll';
  var EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

  function getCookie(name) {
    var match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
    return match ? decodeURIComponent(match[1]) : null;
  }

  function setCookie(name, value, days) {
    var expires = new Date(Date.now() + days * 864e5).toUTCString();
    document.cookie = name + '=' + encodeURIComponent(value) + '; expires=' + expires + '; path=/; SameSite=Lax';
  }

  /* Above 990px Horizon scrolls `.page-wrapper`, not the window
     (see base.css and c-header.js). */
  function scroller() {
    var wrapper = document.querySelector('.page-wrapper');
    if (wrapper && wrapper.scrollHeight > wrapper.clientHeight && getComputedStyle(wrapper).overflowY !== 'visible') {
      return wrapper;
    }
    return null;
  }

  function getScroll() {
    var el = scroller();
    return el ? el.scrollTop : window.scrollY || 0;
  }

  function setScroll(y) {
    var el = scroller();
    if (el) el.scrollTop = y;
    else window.scrollTo(0, y);
  }

  function cartUpdateUrl() {
    return (window.Theme && window.Theme.routes && window.Theme.routes.cart_update_url) || '/cart/update.js';
  }

  /* Adds the code to whatever is already on the cart; the cart keeps it until
     checkout even while empty. */
  function applyCode(code) {
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (res) {
        return res.json();
      })
      .then(function (cart) {
        var codes = (cart.discount_codes || []).map(function (d) {
          return d.code;
        });
        var has = codes.some(function (c) {
          return c.toUpperCase() === code;
        });
        if (has) return null;
        codes.push(code);
        return fetch(cartUpdateUrl(), {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ discount: codes.join(',') }),
        });
      })
      .catch(function () {
        /* The email still carries the code; never block the success state. */
      });
  }

  function init(root) {
    if (!root || root.__cWpReady) return;
    root.__cWpReady = true;

    var designMode = root.hasAttribute('data-design-mode');
    var code = root.getAttribute('data-code');
    var delay = (parseInt(root.getAttribute('data-delay'), 10) || 0) * 1000;
    var closedDays = parseInt(root.getAttribute('data-cookie-days'), 10) || 30;
    var errorText = root.getAttribute('data-error');

    var scrim = root.querySelector('[data-c-wp-scrim]');
    var dialog = root.querySelector('[data-c-wp-dialog]');
    var offer = root.querySelector('[data-c-wp-offer]');
    var success = root.querySelector('[data-c-wp-success]');
    var successHeading = root.querySelector('[data-c-wp-success-heading]');
    var form = root.querySelector('.c-wp__form');
    var input = root.querySelector('[data-c-wp-email]');
    var error = root.querySelector('[data-c-wp-error]');
    var submit = root.querySelector('[data-c-wp-submit]');
    var emailOut = root.querySelector('[data-c-wp-email-out]');
    var pill = root.querySelector('[data-c-wp-pill]');
    var posted = root.querySelector('[data-c-wp-posted]');
    var failed = root.querySelector('[data-c-wp-failed]');
    var lastFocus = null;

    function isOpen() {
      return !scrim.hidden;
    }

    function open(state) {
      lastFocus = document.activeElement;
      offer.hidden = state === 'success';
      success.hidden = state !== 'success';
      pill.hidden = true;
      scrim.hidden = false;
      document.documentElement.setAttribute('scroll-lock', '');
      (state === 'success' ? successHeading : dialog).focus({ preventScroll: true });
    }

    function close() {
      if (!isOpen()) return;
      scrim.hidden = true;
      document.documentElement.removeAttribute('scroll-lock');

      if (designMode) return;

      if (getCookie(STATE_COOKIE) !== 'joined') {
        setCookie(STATE_COOKIE, 'closed', closedDays);
        if (getCookie(PILL_COOKIE) !== 'hidden') pill.hidden = false;
      }
      if (lastFocus && document.contains(lastFocus) && lastFocus !== document.body) lastFocus.focus();
    }

    function showError(message) {
      error.textContent = message;
      error.hidden = false;
      input.setAttribute('aria-invalid', 'true');
      input.focus();
    }

    function succeed(email) {
      if (emailOut) emailOut.textContent = email;
      if (!designMode) setCookie(STATE_COOKIE, 'joined', 365);
      return applyCode(code).then(function () {
        if (isOpen()) {
          offer.hidden = true;
          success.hidden = false;
          successHeading.focus({ preventScroll: true });
        } else {
          open('success');
        }
      });
    }

    form.addEventListener('submit', function (event) {
      var email = input.value.trim();

      error.hidden = true;
      input.removeAttribute('aria-invalid');
      if (!EMAIL.test(email)) {
        /* Stop here, before Shopify's captcha script sees the submit. */
        event.preventDefault();
        event.stopPropagation();
        showError(errorText);
        return;
      }
      input.value = email;
      submit.setAttribute('aria-busy', 'true');

      try {
        sessionStorage.setItem(PENDING_KEY, '1');
        sessionStorage.setItem(EMAIL_KEY, email);
        sessionStorage.setItem(SCROLL_KEY, String(getScroll()));
      } catch (e) {}
      /* No preventDefault: Shopify's captcha runs, then the page posts. */
    });

    root.addEventListener('click', function (event) {
      if (event.target === scrim || event.target.closest('[data-c-wp-close]')) {
        close();
        return;
      }
      if (event.target.closest('[data-c-wp-reopen]')) {
        open('offer');
        return;
      }
      if (event.target.closest('[data-c-wp-hide]')) {
        pill.hidden = true;
        setCookie(PILL_COOKIE, 'hidden', closedDays);
      }
    });

    root.addEventListener('keydown', function (event) {
      if (!isOpen()) return;
      if (event.key === 'Escape') {
        close();
        return;
      }
      if (event.key !== 'Tab') return;

      /* Keep focus inside the dialog. */
      var focusables = Array.prototype.filter.call(
        dialog.querySelectorAll('a[href], button:not([disabled]), input:not([type="hidden"])'),
        function (el) {
          return el.offsetParent !== null;
        }
      );
      if (!focusables.length) return;
      var first = focusables[0];
      var last = focusables[focusables.length - 1];
      if (event.shiftKey && (document.activeElement === first || document.activeElement === dialog)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    /* ---------- First paint ---------- */

    if (designMode) {
      open('offer');
      return;
    }

    var pending = false;
    var pendingEmail = '';
    var pendingScroll = 0;
    try {
      pending = sessionStorage.getItem(PENDING_KEY) === '1';
      pendingEmail = sessionStorage.getItem(EMAIL_KEY) || '';
      pendingScroll = parseInt(sessionStorage.getItem(SCROLL_KEY), 10) || 0;
      sessionStorage.removeItem(PENDING_KEY);
      sessionStorage.removeItem(EMAIL_KEY);
      sessionStorage.removeItem(SCROLL_KEY);
    } catch (e) {}

    var postedParam = /[?&]customer_posted=true/.test(window.location.search);

    if (pending) {
      /* Back from our own post: undo Shopify's #contact_form jump and the
         query flag, and skip the entrance animation. */
      root.classList.add('c-wp--instant');
      try {
        var params = new URLSearchParams(window.location.search);
        params.delete('customer_posted');
        var query = params.toString();
        history.replaceState(null, '', window.location.pathname + (query ? '?' + query : ''));
      } catch (e) {}
      setScroll(pendingScroll);
      window.requestAnimationFrame(function () {
        setScroll(pendingScroll);
      });
    }
    /* Shopify sends the post back to this page with ?customer_posted=true;
       either signal counts, an error marker wins. */
    var postedOk = !failed && (posted || postedParam);
    if (pending && postedOk) {
      succeed((posted && posted.getAttribute('data-email')) || pendingEmail);
      return;
    }
    if (pending && failed) {
      /* Shopify rejected the post (it re-renders the form with errors). */
      open('offer');
      input.value = pendingEmail;
      showError(errorText);
      return;
    }

    var state = getCookie(STATE_COOKIE);
    if (state === 'joined') return;
    if (state === 'closed') {
      if (getCookie(PILL_COOKIE) !== 'hidden') pill.hidden = false;
      return;
    }

    function schedule() {
      window.setTimeout(function () {
        if (getCookie(STATE_COOKIE)) return;
        open('offer');
      }, delay);
    }

    var gate = document.querySelector('[data-fm-age-gate]');
    if (getCookie(GATE_COOKIE) === '1' || !gate) {
      schedule();
    } else {
      var confirm = gate.querySelector('[data-fm-age-confirm]');
      if (confirm) confirm.addEventListener('click', schedule, { once: true });
    }
  }

  function initAll(scope) {
    /* The signup iframe loads a full storefront page; stay out of it. */
    if (window.self !== window.top) return;
    var roots = (scope || document).querySelectorAll('[data-c-wp]');
    Array.prototype.forEach.call(roots, init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
    });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
})();
